import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import {
  applyStartTimeChange,
  endTimeOptions,
  nextBandRow,
  startTimeOptions,
  type PriceBandRowFormState,
  type PriceBandRowsErrors,
} from '@/lib/venue/priceViewModel'

/**
 * 요금 구간 행 편집기 — **요일유형 축과 날짜 축이 함께 쓴다**(설계 §아키텍처 개요).
 *
 * 🔑 요구 (3): 시작·종료 select 의 `option` 목록 자체가 **이미 등록된 구간과 겹치지 않는 시각만** 담는다.
 * 판정은 전부 `priceViewModel` 의 순수 함수이고 이 컴포넌트는 조립만 한다(설계 D5·D6).
 */
export function PriceBandEditor({
  axisLabel,
  rows,
  onChange,
  disabled,
  maxRows,
  unitMinutes,
  errors,
  notice,
}: {
  /** 오류 문구·접근성 라벨 앞에 붙는 축 이름(예: "평일", "2027-02-06"). */
  axisLabel: string
  rows: PriceBandRowFormState[]
  onChange: (rows: PriceBandRowFormState[]) => void
  disabled: boolean
  /** 🔒 서버가 준 상한(`maxPriceBandsPerDayType`) — 하드코딩하지 않는다. */
  maxRows: number
  /** 🔒 코트의 예약 단위(`reservationUnitMinutes`) — 시작·종료 select 후보 시각의 격자 간격(`#876`). */
  unitMinutes: number
  errors?: PriceBandRowsErrors
  /** 구간이 운영시간 밖이라 효과가 없을 때 서버가 만든 문구. */
  notice?: string | null
}) {
  const nextRow = nextBandRow(rows)
  const addBlocked = nextRow === null
  const limitReached = rows.length >= maxRows

  function updateRow(index: number, patch: Partial<PriceBandRowFormState>) {
    onChange(rows.map((row, rowIndex) => (rowIndex === index ? { ...row, ...patch } : row)))
  }

  return (
    <div className="space-y-3">
      {rows.length === 0 ? (
        <p className="text-xs text-slateish-500">
          설정된 시간대가 없어요. 이 유형의 모든 시간에 기본 요금이 적용됩니다.
        </p>
      ) : null}

      {rows.map((row, index) => {
        const startOptions = startTimeOptions(rows, index, unitMinutes)
        const endOptions = endTimeOptions(rows, index, row.startTime, unitMinutes)
        const rowError = errors?.rowErrors[String(index)]

        return (
          <div
            key={index}
            className="flex flex-wrap items-center gap-2 rounded-lg border border-slateish-200 px-3 py-2.5"
          >
            <Select
              aria-label={`${axisLabel} 구간 ${index + 1} 시작`}
              className="w-28"
              disabled={disabled}
              value={row.startTime}
              onChange={(event) => {
                onChange(applyStartTimeChange(rows, index, event.target.value, unitMinutes))
              }}
            >
              {/* 현재 값이 선택지에서 빠진 경우(다른 행이 그 시각을 덮은 직후)에도 표시는 유지한다. */}
              {startOptions.includes(row.startTime) ? null : (
                <option value={row.startTime}>{row.startTime}</option>
              )}
              {startOptions.map((time) => (
                <option key={time} value={time}>
                  {time}
                </option>
              ))}
            </Select>
            <span className="text-slateish-400">~</span>
            <Select
              aria-label={`${axisLabel} 구간 ${index + 1} 종료`}
              className="w-28"
              disabled={disabled || endOptions.length === 0}
              value={row.endTime}
              onChange={(event) => {
                updateRow(index, { endTime: event.target.value })
              }}
            >
              {endOptions.includes(row.endTime) ? null : (
                <option value={row.endTime}>{row.endTime}</option>
              )}
              {endOptions.map((time) => (
                <option key={time} value={time}>
                  {time}
                </option>
              ))}
            </Select>
            <Input
              aria-label={`${axisLabel} 구간 ${index + 1} 시간당 요금`}
              className="w-32"
              inputMode="numeric"
              placeholder="시간당 요금"
              disabled={disabled}
              value={row.hourlyFee}
              aria-invalid={Boolean(rowError)}
              onChange={(event) => {
                updateRow(index, { hourlyFee: event.target.value })
              }}
            />
            <Button
              variant="ghost"
              size="sm"
              disabled={disabled}
              onClick={() => {
                onChange(rows.filter((_, rowIndex) => rowIndex !== index))
              }}
            >
              {`${axisLabel} 구간 ${index + 1} 삭제`}
            </Button>
            {rowError ? <p className="w-full text-xs text-danger-500">{rowError}</p> : null}
          </div>
        )
      })}

      {errors?.general ? <p className="text-xs text-danger-500">{errors.general}</p> : null}
      {notice ? <p className="text-xs text-warning-500">{notice}</p> : null}

      <div className="flex items-center gap-3">
        <Button
          variant="secondary"
          size="sm"
          disabled={disabled || addBlocked || limitReached}
          onClick={() => {
            if (nextRow) onChange([...rows, nextRow])
          }}
        >
          {`${axisLabel} 구간 추가`}
        </Button>
        {addBlocked ? (
          <p className="text-xs text-slateish-500">빈 시간대가 없습니다.</p>
        ) : limitReached ? (
          <p className="text-xs text-slateish-500">{`구간은 최대 ${maxRows}개까지 설정할 수 있어요.`}</p>
        ) : null}
      </div>

      <p className="text-xs text-slateish-500">
        예약 칸이 구간 경계를 걸치면 분 단위로 나눠 계산돼요.
      </p>
    </div>
  )
}
